/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Step, CallBackProps, TooltipRenderProps } from 'react-joyride'

import { trackInternalEvent } from '@rio/tracking'
import { JoyRide, setElementStyles } from 'builder/components/JoyRide'

import { useI18n } from 'builder/hooks/useI18n'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useFeaturesConfig } from 'builder/hooks/featureConfig/useFeaturesConfig'

import { Tooltip } from './Tooltip/Tooltip'
import { animationStyles, fadeOutStyles, getFloaterStyles } from './utils'
import { useDashV4Tour } from './useDashV4Tour'
import { AnimatedKeyframes } from './styles'

const TRANSLATION = 'builder.dashboard.tour_v4'

export const DashV4Tour = () => {
  const { i18n } = useI18n()
  const { features } = useFeaturesConfig()
  const { isPhone, isTablet } = useMediaQueries()
  const { run, setTourStatus, scrollOnTopOnFinish } = useDashV4Tour()

  const steps: Step[] = [
    {
      offset: 0,
      disableBeacon: true,
      target: '#dashboard-progress-tracker',
      placement: isPhone ? 'bottom' : 'top',
      title: i18n.t(`${TRANSLATION}.step_1.title`),
      content: i18n.t(`${TRANSLATION}.step_1.content`),
      spotlightPadding: 0,
      styles: { spotlight: { borderRadius: 0 } },
      floaterProps: { styles: getFloaterStyles({ step: 1, isPhone, isTablet }) },
    },
    {
      offset: isPhone ? 0 : isTablet ? -10 : -21,
      target: '#dashboard-recommended-jobs',
      placement: isPhone ? 'top' : 'top-start',
      title: i18n.t(`${TRANSLATION}.step_2.title`),
      content: i18n.t(`${TRANSLATION}.step_2.content`),
      spotlightPadding: -1,
      styles: { spotlight: { borderRadius: 12 } },
      floaterProps: {
        styles: getFloaterStyles({ step: 2, isPhone, isTablet }),
      },
    },
  ]

  if (features.learningHub) {
    steps.push({
      offset: isPhone ? 0 : isTablet ? -10 : -21,
      target: '#learning-content-dashboard',
      placement: isPhone ? 'top' : 'top-start',
      title: i18n.t(`${TRANSLATION}.step_3.title`),
      content: i18n.t(`${TRANSLATION}.step_3.content`),
      spotlightPadding: -1,
      styles: { spotlight: { borderRadius: 12 } },
      floaterProps: { styles: getFloaterStyles({ step: 3, isPhone, isTablet }) },
    })
  }

  const onNext = (
    e: React.MouseEvent<HTMLElement>,
    callback: (e: React.MouseEvent<HTMLElement>) => void,
  ) => {
    setElementStyles('.__floater', fadeOutStyles)
    setElementStyles('.react-joyride__overlay', fadeOutStyles)

    setTimeout(() => callback(e), 300)
  }

  const onCallback = (data: CallBackProps) => {
    if (['skip', 'close'].includes(data.action)) {
      setTourStatus(true)
      scrollOnTopOnFinish()
      trackInternalEvent('learning_onboarding_skip')
      setElementStyles('#learning-content-dashboard', { padding: '0', margin: '0' })
    }

    if (data.type === 'tooltip') {
      // @ts-ignore
      trackInternalEvent(`learning_onboarding_step_${data.index + 1}`)

      setElementStyles('.react-joyride__spotlight', animationStyles)

      // This index check is to ensure that the styles are applied correctly
      // for the third step when the learning hub is enabled.
      if (data.index === 2) {
        setElementStyles('#learning-content-dashboard', { padding: '24px', margin: '-24px' })
      }

      if (!isPhone && (data.index === 1 || data.index === 2)) {
        setElementStyles('.__floater__arrow', { left: isTablet ? '-150px' : '-196px' })
      }
    }
  }

  if (!features.learningHub) return null

  return (
    <>
      <AnimatedKeyframes />
      <JoyRide
        run={run}
        steps={steps}
        callback={onCallback}
        showSkipButton={false}
        styles={{
          options: { zIndex: 10000 },
          overlay: { ...animationStyles },
        }}
        scrollOffset={isPhone ? 141 : 270}
        scrollDuration={1100}
        disableScrolling={false}
        customToolTip={(props: TooltipRenderProps) => (
          <Tooltip
            {...props}
            onNext={onNext}
            skipButtonLabel={i18n.t(`${TRANSLATION}.skip_button`)}
            nextButtonLabel={i18n.t(`${TRANSLATION}.next_button`)}
            finalButtonLabel={i18n.t(`${TRANSLATION}.final_button`)}
          />
        )}
      />
    </>
  )
}
